import React, { useContext, useCallback, useState, useEffect, useMemo } from 'react';
import { Grid, makeStyles, CircularProgress, Theme } from '@material-ui/core';
import clsx from 'clsx';
import { Monitor, MonitorTargetType, MonitorAction, MonitorActionType, CreateIssueMonitorAction, SendEmailMonitorAction, CreateTicketMonitorAction,
    SlackMessageMonitorAction } from '@moderepo/mode-apis';
import { modeUIContext, showSuccessNotification, showDialog, DialogType, DialogResponse, DialogResponseCode, setIsEditingForm, SlideOutPanelLocation,
    hideSlideOutPanel, showSlideOutPanel } from '@moderepo/mode-ui-state';
import { Agent } from '@moderepo/mode-internal-apis';
import { BaseCompAction, capitalizeAll, capitalizeFirst, SlideOutComp, TimeUnit } from '@moderepo/mode-app-components';
import { ritzDataContext, selectProjectAgent, selectProjectAgents, selectProjectById, selectLoggedInAgent, fetchProjectAgents }
    from '@moderepo/mode-data-state-ritz';
import { createMonitor, deleteMonitor, fetchMonitor, fetchRobotCatalog, fetchRobotCloudProjectSettings, rcDataContext, selectMonitorById,
    selectRobotById, selectRobotCatalog, selectRobotCloudProjectSettings, updateMonitor } from '@moderepo/mode-data-state-robot-cloud';
import { useRitzTranslation } from '../../utils/customHooks';
import { getDateTimeAndElapsedTime } from '../containerUtils';
import { RCMonitorInfo, RCMonitorEditor } from '../../components/robot-cloud';
import * as RCUtils from './RCUtils';
import { CreateMonitorStep, RCMonitorEditorUpdatableInputs } from '../../components/robot-cloud/monitor-editor/RCMonitorEditor';
import { RCMonitorPreviewCompOptions, RCSlideOutPanelCompType } from './RCProjectConfig';



const useStyle = makeStyles((theme: Theme) => {
    return {
        // Edit Monitor form requires a larger width therefore we need to override the container's width
        [theme.breakpoints.up('md')]: {
            root: {
                '& .slide-out-comp-container.is-editing': {
                    minWidth: 700,
                },
            },
        },

        [theme.breakpoints.up('lg')]: {
            root: {
                '& .slide-out-comp-container.is-editing': {
                    minWidth: 900,
                },
            },
        },

        primaryAction: {
            '&.enable-action': {
                background: theme.palette.success.main,
                color     : theme.palette.success.contrastText,
            },
            '&.disable-action': {
                background: theme.palette.error.main,
                color     : theme.palette.error.contrastText,
            },
        },

        secondaryAction: {
            '&.remove': {
                '& .icon': {
                    color: theme.palette.secondary.main,
                },
            },
        },
    };
}, { name: 'RCMonitorPreviewContainer', index: 1 });



/**
 * This custom hook is used for converting an Array of MonitorAction to a map so that it is easier to lookup Monitor action config by type
 * @param actionsArray
 */
const useActionArrayToMap = (actionsArray: readonly MonitorAction[] | undefined): {[actionType: string]: MonitorAction} => {
    const [actionsMap, setActionsMap] = useState<{[actionType: string]: MonitorAction}>({});

    useEffect(() => {
        if (actionsArray) {
            setActionsMap(actionsArray.reduce((
                result: {[actionType: string]: MonitorAction}, action: MonitorAction,
            ): {[actionType: string]: MonitorAction} => {
                return {
                    ...result,
                    [action.type]: action,
                };
            }, {}));
        } else {
            setActionsMap({});
        }
    }, [actionsArray]);

    return actionsMap;
};



interface RCMonitorPreviewContainerProps {
    readonly projectId: number;
    readonly monitorId: number | undefined;
    readonly preSelectedRobotClass?: string;
    readonly preSelectedRobotId?: string;
    readonly preSelectedHomeId?: number;
}


/**
 * This container display an Monitor Condition details
 */
export const RCMonitorPreviewContainer = (props: RCMonitorPreviewContainerProps) => {
    const { dispatch:uiDispatch } = useContext(modeUIContext);
    const { state:dataState, dispatch:dataDispatch } = useContext(ritzDataContext);
    const { state:rcDataState, dispatch:rcDataDispatch } = useContext(rcDataContext);
    const loggedInAgent = selectLoggedInAgent(dataState) as Agent;
    const { projectId } = props;
    const project = selectProjectById(dataState, projectId);
    const projectAgents = selectProjectAgents(dataState, projectId);

    const selectedMonitor = props.monitorId ? selectMonitorById(rcDataState, props.monitorId) : undefined;
    const isNewMonitor = !props.monitorId;
    const robotCatalog = selectRobotCatalog(rcDataState, projectId);
    const rcProjectSettings = selectRobotCloudProjectSettings(rcDataState, projectId);


    // The robot and agents tied to the selected Monitor. We need the robot info and agent info later on
    const monitorRobot = selectRobotById(rcDataState, selectedMonitor?.targetHomeId, selectedMonitor?.targetRobotId);
    const monitorCreatedAgent = selectedMonitor && selectedMonitor.createdAgentId
        ? selectProjectAgent(dataState, projectId, selectedMonitor.createdAgentId) : undefined;
    const monitorUpdatedAgent = selectedMonitor && selectedMonitor.updatedAgentId
        ? selectProjectAgent(dataState, projectId, selectedMonitor.updatedAgentId) : undefined;

    const monitorActionsMap = useActionArrayToMap(selectedMonitor?.actions);


    const { trans } = useRitzTranslation(project);
    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [editingStep, setEditingStep] = useState<CreateMonitorStep | undefined>();
    const classes = useStyle();


    /**
     * Update the selected entity to select a different Monitor or unselect an Monitor
     */
    const updateSelectedEntity = useCallback((monitorId: number | undefined) => {
        if (monitorId) {
            const options: RCMonitorPreviewCompOptions = {
                location : SlideOutPanelLocation.RIGHT,
                compType : RCSlideOutPanelCompType.MONITOR_PREVIEW,
                compProps: {
                    data: {
                        projectId,
                        monitorId,
                    },
                },
            };
            uiDispatch(showSlideOutPanel(options));
        } else {
            uiDispatch(hideSlideOutPanel());
        }
    }, [projectId, uiDispatch]);


    /**
     * Turn on/off edit mode LOCALLY and GLOBALLY
     * @param value - The edit mode true/false
     */
    const onChangeEditingMode = useCallback((value: boolean) => {
        // turn on/off editing mode GLOBALLY so that the system can disable all other components
        uiDispatch(setIsEditingForm(value));

        // turn on/off editing mode LOCALLY so that we can enable this component
        setIsEditing(value);
    }, [uiDispatch]);


    /**
     * Automatically set editing mode to true on component init if props.monitorId is undefined
     */
    useEffect(() => {
        if (props.monitorId === undefined) {
            onChangeEditingMode(true);
        }
    }, [props.monitorId, onChangeEditingMode]);


    // This useEffect is used for loading Monitor data for the selected Monitor if we don't have the data yet
    useEffect(() => {
        if (props.monitorId && !selectedMonitor) {
            rcDataDispatch(fetchMonitor(projectId, props.monitorId)).catch(() => {
                // ignore error because dataDispatch already handled it
            });
        }
    }, [props.monitorId, projectId, selectedMonitor, rcDataDispatch]);


    /**
     * This use effect is used for loading createAgent data when an Monitor is selected. The Monitor data contains only createdAgentId but we also
     * need other information about the agent so we need to load agent details if we don't have the details yet
     */
    useEffect(() => {
        // If an Monitor is selected but we don't have agent data, load agent data. However, there is no API for fetching 1 agent so we will fetch all
        // agents belonged to the project and then lookup agent data from that list
        if (selectedMonitor && (selectedMonitor.createdAgentId || selectedMonitor.updatedAgentId) && !projectAgents) {
            dataDispatch(fetchProjectAgents(projectId)).catch(() => {
                // ignore error because dataDispatch already took care of the error
            });
        }
    }, [dataDispatch, projectId, selectedMonitor, projectAgents]);



    /**
     * This useEffect is used for loading robot definition if it is not loaded
     */
    useEffect(() => {
        if (!robotCatalog) {
            rcDataDispatch(fetchRobotCatalog(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [robotCatalog, projectId, rcDataDispatch]);



    useEffect(() => {
        // If project is a RobotCLoud project, load robotCloud project settings
        if (!rcProjectSettings) {
            rcDataDispatch(fetchRobotCloudProjectSettings(projectId)).catch(() => {
                // ignore error because dataDispatch already handled the error
            });
        }
    }, [rcProjectSettings, projectId, rcDataDispatch]);


    /**
     * end editing mode
     */
    const closeEditing = useCallback(() => {
        setEditingStep(undefined);
        onChangeEditingMode(false);
    }, [onChangeEditingMode]);



    /**
     * Handler for when the user click Save input from the user form.
     */
    const onEditMonitorFormSubmit = useCallback(async (inputs: RCMonitorEditorUpdatableInputs) => {
        const { data, monitorId } = inputs;

        if (monitorId !== undefined) {
            // If we have monitorId, the user must be editing an Monitor
            try {
                await rcDataDispatch(updateMonitor(projectId, loggedInAgent.id, monitorId, data));

                uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.monitor' }));

                // refetch the Monitor data
                await rcDataDispatch(fetchMonitor(projectId, monitorId));
                closeEditing();
            } catch (error) {
                // Ignore error because dataDispatch already took care of the error
            }
        } else {
            // there is no monitorId which mean the user must be creating a new Monitor
            try {
                // Backend will return an Object with the new Monitor id.
                await rcDataDispatch(createMonitor(projectId, loggedInAgent.id, data));
                closeEditing();

                uiDispatch(showSuccessNotification('notif.generic_item_created_successfully', { term: 'label.monitor' }));
                updateSelectedEntity(undefined);
            } catch (error) {
                // Ignore error because dataDispatch already took care of the error
            }
        }
    }, [rcDataDispatch, projectId, loggedInAgent.id, uiDispatch, closeEditing, updateSelectedEntity]);



    const onRemoveMonitorClicked = useCallback((monitor: Monitor) => {
        // Confirm before deleting Monitor
        uiDispatch(showDialog({
            type          : DialogType.CONFIRM,
            title         : 'dialog.confirm_remove_monitor.title',
            message       : 'dialog.confirm_remove_monitor.message',
            messageData   : { monitor_name: monitor.name },
            positiveButton: 'label.delete',
            negativeButton: 'label.cancel',
            onClose       : async (response?: DialogResponse): Promise<void> => {
                if (response && response.code === DialogResponseCode.POSITIVE) {
                    rcDataDispatch(deleteMonitor(projectId, monitor.id)).then(() => {
                        uiDispatch(showSuccessNotification('notif.generic_item_deleted_successfully', { term: 'label.monitor' }));

                        // if the current selected Monitor is the Monitor we just deleted, unselect it.
                        updateSelectedEntity(undefined);
                    }).catch(() => {
                        // ignore error
                    });
                }
            },
        }));
    }, [uiDispatch, rcDataDispatch, projectId, updateSelectedEntity]);


    /**
     * Toggle Monitor condition's disabled state.
     */
    const toggleMonitorDisabledState = useCallback(async () => {
        if (selectedMonitor) {
            const continueToggleDisableState = async () => {
                try {
                    await rcDataDispatch(updateMonitor(projectId, loggedInAgent.id, selectedMonitor.id, {
                        disabled: !selectedMonitor.disabled,
                    }));

                    // Reload Monitor
                    await rcDataDispatch(fetchMonitor(projectId, selectedMonitor.id));
                    uiDispatch(showSuccessNotification('notif.generic_item_updated_successfully', { term: 'label.monitor' }));
                } catch (error) {
                    // / ignore error because dataDispatch already handled the error
                }
            };

            // disable an Monitor condition can be dangerous so we need to confirm with the user to make sure it is not a mistake.
            // For enable Monitor condition, we don't need to confirm
            if (!selectedMonitor.disabled) {
                uiDispatch(showDialog({
                    type          : DialogType.CONFIRM,
                    title         : 'dialog.confirm_disable_monitor.title',
                    message       : 'dialog.confirm_disable_monitor.message',
                    messageData   : { monitor_name: selectedMonitor.name },
                    positiveButton: 'label.disable',
                    negativeButton: 'label.cancel',
                    onClose       : async (response?: DialogResponse): Promise<void> => {
                        if (response && response.code === DialogResponseCode.POSITIVE) {
                            continueToggleDisableState();
                        }
                    },
                }));
            } else {
                continueToggleDisableState();
            }
        }
    }, [selectedMonitor, rcDataDispatch, projectId, loggedInAgent.id, uiDispatch]);



    /**
     * onCancelEdit click, we will stop editing mode. And if we are in create new Monitor mode, we will have to also unselect the current
     * selected Monitor
     */
    const onCancelEdit = useCallback(() => {
        closeEditing();
        if (isNewMonitor) {
            updateSelectedEntity(undefined);
        }
    }, [isNewMonitor, closeEditing, updateSelectedEntity]);



    /**
     * Create inputSettings for the RCMonitorEditor. Since we don't know the input we will have ahead of time, we can only setup the
     * cancelButton and saveButtons. Everything else will be created dynamically.
     */
    const fieldsSettings = useMemo(() => {
        return {};
    }, []);



    const primaryActions = useMemo(() => {
        if (selectedMonitor) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.primaryAction, selectedMonitor.disabled ? 'enable-action' : 'disable-action'),
                label    : selectedMonitor.disabled ? (
                    capitalizeAll(trans('label.enable'))
                ) : (
                    capitalizeAll(trans('label.disable'))
                ),
                onClick: toggleMonitorDisabledState,
            }];

            return actions;
        }
        return undefined;
    }, [classes.primaryAction, selectedMonitor, toggleMonitorDisabledState, trans]);


    const secondaryActions = useMemo(() => {
        if (selectedMonitor) {
            const actions: BaseCompAction[] = [{
                className: clsx(classes.secondaryAction, 'remove'),
                icon     : 'delete',
                label    : capitalizeAll(trans('label.monitor_action.delete_monitor')),
                onClick  : () => {
                    onRemoveMonitorClicked(selectedMonitor);
                },
            }];

            return actions;
        }

        return undefined;
    }, [classes.secondaryAction, onRemoveMonitorClicked, selectedMonitor, trans]);


    const slideOutCompContent = useMemo(() => {
        if (selectedMonitor || !props.monitorId) {
            if (selectedMonitor && !isEditing) {
                return (
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <RCMonitorInfo
                                className={clsx('transparent', isEditing && 'disabled')}
                                formProps={{
                                    className: 'single-column',
                                }}
                                isEditing={isEditing}
                                title={capitalizeAll(trans('label.monitor_settings'))}
                                icon="settings"
                                fields={{
                                    id: {
                                        label       : capitalizeAll(trans('label.id')),
                                        displayValue: selectedMonitor.id.toString(),
                                    },
                                    status: {
                                        label       : capitalizeAll(trans('label.status')),
                                        displayValue: selectedMonitor.disabled
                                            ? capitalizeAll(trans('label.disabled')) : capitalizeAll(trans('label.enabled')),
                                    },
                                    name: {
                                        label       : capitalizeAll(trans('label.monitor_name')),
                                        displayValue: selectedMonitor.name ? selectedMonitor.name : capitalizeAll(trans('label.none')),
                                        editable    : true,
                                    },
                                    description: {
                                        label       : capitalizeAll(trans('label.monitor_description')),
                                        displayValue: selectedMonitor.description
                                            ? selectedMonitor.description : capitalizeAll(trans('label.none')),
                                        editable: true,
                                    },
                                    severity: {
                                        label       : capitalizeAll(trans('label.severity')),
                                        displayValue: selectedMonitor.severity
                                            ? capitalizeAll(trans(RCUtils.getMonitorSeverityName(selectedMonitor.severity)))
                                            : capitalizeAll(trans('label.none')),
                                        value   : selectedMonitor.severity,
                                        editable: true,
                                    },
                                    target: {
                                        label       : capitalizeAll(trans('label.monitor_target')),
                                        displayValue: selectedMonitor.targetType === MonitorTargetType.ROBOT_ID
                                            ? capitalizeAll(trans('label.robot_and_robot_id', {
                                                robot_id: selectedMonitor.targetRobotId,
                                            }))
                                            : capitalizeAll(trans('label.robot_class_and_class_name', {
                                                class_name: selectedMonitor.targetRobotClass,
                                            })),
                                        editable: true,
                                    },
                                    targetHomeId: {
                                        label       : capitalizeAll(trans('label.target_home_id')),
                                        displayValue: String(selectedMonitor.targetHomeId || capitalizeAll(trans('label.none'))),
                                    },
                                    conditions: {
                                        label       : capitalizeAll(trans('label.condition')),
                                        displayValue: RCUtils.getMonitorConditionAsString(
                                            robotCatalog,
                                            selectedMonitor.condition, selectedMonitor.targetRobotId
                                                ? monitorRobot?.robotClass : selectedMonitor.targetRobotClass, trans, false,
                                        ),
                                        editable: true,
                                    },
                                    sampleInterval: {
                                        label       : capitalizeAll(trans('label.sample_interval')),
                                        displayValue: selectedMonitor.sampleInterval !== undefined
                                            ? trans('label.value_unit_display', {
                                                value: selectedMonitor.sampleInterval,
                                                unit : trans(TimeUnit.SECOND, { count: 2 }),
                                            }) : '',
                                        value   : selectedMonitor.sampleInterval,
                                        editable: true,
                                    },
                                    treatMissingDataAs: {
                                        label       : capitalizeFirst(trans('label.treat_missing_data_as')),
                                        displayValue: selectedMonitor.treatMissingDataAs
                                            ? capitalizeFirst(trans(
                                                RCUtils.getMissingDataTreatmentTypeName(selectedMonitor.treatMissingDataAs),
                                            )) : '',
                                        value   : selectedMonitor.treatMissingDataAs,
                                        editable: true,
                                    },
                                    createIssueAction: monitorActionsMap[MonitorActionType.CREATE_ISSUE]
                                        ? {
                                            description: {
                                                label       : capitalizeAll(trans('label.description')),
                                                displayValue: (monitorActionsMap[MonitorActionType.CREATE_ISSUE] as CreateIssueMonitorAction)
                                                    .parameters.description,
                                                editable: true,
                                            },
                                            autoResolveIssue: {
                                                label       : capitalizeAll(trans('label.auto_resolve_issue')),
                                                displayValue: (monitorActionsMap[MonitorActionType.CREATE_ISSUE] as CreateIssueMonitorAction)
                                                    .recoveryParameters ? capitalizeFirst(trans('label.yes'))
                                                    : capitalizeFirst(trans('label.no')),
                                                editable: true,
                                                hidden  : !monitorActionsMap[MonitorActionType.CREATE_ISSUE].recoveryParameters,
                                            },
                                        } : undefined,
                                    sendEmailAction: monitorActionsMap[MonitorActionType.SEND_EMAIL]
                                        ? {
                                            email: {
                                                label       : capitalizeAll(trans('label.email')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SEND_EMAIL] as SendEmailMonitorAction)
                                                    .parameters.email,
                                                editable: true,
                                            },
                                            subject: {
                                                label       : capitalizeAll(trans('label.email_subject')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SEND_EMAIL] as SendEmailMonitorAction)
                                                    .parameters.subject,
                                                editable: true,
                                            },
                                            body: {
                                                label       : capitalizeAll(trans('label.email_body')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SEND_EMAIL] as SendEmailMonitorAction)
                                                    .parameters.body,
                                                editable: true,
                                            },
                                            recoverBody: {
                                                label       : capitalizeAll(trans('label.recovery_email_body')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SEND_EMAIL] as SendEmailMonitorAction)
                                                    .recoveryParameters?.body || '',
                                                editable: true,
                                                hidden  : !monitorActionsMap[MonitorActionType.SEND_EMAIL].recoveryParameters,
                                            },
                                        } : undefined,
                                    createTicketAction: monitorActionsMap[MonitorActionType.CREATE_TICKET]
                                        ? {
                                            systemType: {
                                                label       : capitalizeAll(trans('label.ticket_system_type')),
                                                displayValue: (monitorActionsMap[MonitorActionType.CREATE_TICKET] as CreateTicketMonitorAction)
                                                    .parameters.systemType,
                                                editable: true,
                                            },
                                            parameters: {
                                                label       : capitalizeAll(trans('label.ticket_parameters_json')),
                                                displayValue: JSON.stringify(
                                                    (monitorActionsMap[MonitorActionType.CREATE_TICKET] as CreateTicketMonitorAction)
                                                        .parameters.parameters, undefined, 4,
                                                ),
                                                editable: true,
                                                hidden  : true,
                                            },
                                        } : undefined,
                                    slackMessageAction: monitorActionsMap[MonitorActionType.SLACK_MESSAGE]
                                        ? {
                                            channelId: {
                                                label       : capitalizeAll(trans('label.slack_channel_id')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .parameters.channelId,
                                                editable: true,
                                            },
                                            message: {
                                                label       : capitalizeAll(trans('label.message')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .parameters.message,
                                                editable: true,
                                            },
                                            iconEmoji: {
                                                label       : capitalizeAll(trans('label.slack_icon_emoji')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .parameters.iconEmoji,
                                                editable: true,
                                            },
                                            username: {
                                                label       : capitalizeAll(trans('label.slack_mention_username')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .parameters.username,
                                                editable: true,
                                            },
                                            recoverMessage: {
                                                label       : capitalizeAll(trans('label.recovery_slack_message')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .recoveryParameters?.message || '',
                                                editable: true,
                                                hidden  : !monitorActionsMap[MonitorActionType.SLACK_MESSAGE].recoveryParameters,
                                            },
                                            recoverIconEmoji: {
                                                label       : capitalizeAll(trans('label.slack_recovery_icon_emoji')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .recoveryParameters?.iconEmoji ?? '',
                                                editable: true,
                                            },
                                            recoverUsername: {
                                                label       : capitalizeAll(trans('label.slack_recovery_mention_username')),
                                                displayValue: (monitorActionsMap[MonitorActionType.SLACK_MESSAGE] as SlackMessageMonitorAction)
                                                    .recoveryParameters?.username ?? '',
                                                editable: true,
                                            },
                                        } : undefined,


                                    createdAgentId: {
                                        label       : capitalizeAll(trans('label.created_by')),
                                        displayValue: monitorCreatedAgent?.name || selectedMonitor.createdAgentId.toString(),
                                    },
                                    creationTime: {
                                        label       : capitalizeAll(trans('label.date_created')),
                                        displayValue: getDateTimeAndElapsedTime(selectedMonitor.createdAt, trans),
                                    },
                                    updatedAgentId: {
                                        label       : capitalizeAll(trans('label.updated_by')),
                                        displayValue: monitorUpdatedAgent?.name || selectedMonitor.updatedAgentId.toString(),
                                        hidden      : selectedMonitor.createdAt === selectedMonitor.updatedAt,
                                    },
                                    updatedTime: {
                                        label       : capitalizeAll(trans('label.date_updated')),
                                        displayValue: getDateTimeAndElapsedTime(selectedMonitor.updatedAt, trans),
                                        hidden      : selectedMonitor.createdAt === selectedMonitor.updatedAt,
                                    },
                                }}
                                texts={{
                                    monitorTriggerTitle    : capitalizeAll(trans('label.monitor_trigger')),
                                    monitorActionsTitle    : capitalizeAll(trans('label.monitor_action', { count: 2 })),
                                    createIssueActionTitle : capitalizeAll(trans('label.monitor_action_type.create_issue')),
                                    sendEmailActionTitle   : capitalizeAll(trans('label.monitor_action_type.send_email')),
                                    createTicketActionTitle: capitalizeAll(trans('label.monitor_action_type.create_ticket')),
                                    slackMessageActionTitle: capitalizeAll(trans('label.monitor_action_type.slack_message')),
                                }}

                                actions={{
                                    startEdit: {
                                        onClick: () => {
                                            onChangeEditingMode(true);
                                        },
                                    },
                                }}
                                onFieldClicked={(field: string) => {
                                    onChangeEditingMode(true);

                                    // fields used for Actions
                                    const actionFields = [
                                        'createIssueAction.description', 'createIssueAction.autoResolveIssue',
                                        'sendEmailAction.email', 'sendEmailAction.subject', 'sendEmailAction.body',
                                        'sendEmailAction.recoverBody', 'createTicketAction.systemType', 'createTicketAction.parameters',
                                        'slackMessageAction.channelId', 'slackMessageAction.message', 'slackMessageAction.iconEmoji',
                                        'slackMessageAction.username', 'slackMessageAction.recoverMessage', 'slackMessageAction.recoverIconEmoji',
                                        'slackMessageAction.recoverUsername',
                                    ];
                                    if (field === 'target') {
                                        setEditingStep(CreateMonitorStep.TARGET);
                                    } else if (field === 'conditions' || field === 'treatMissingDataAs' || field === 'sampleInterval') {
                                        setEditingStep(CreateMonitorStep.CONDITIONS);
                                    } else if (actionFields.includes(field)) {
                                        setEditingStep(CreateMonitorStep.ACTIONS);
                                    }
                                }}
                            />
                        </Grid>
                    </Grid>
                );
            } if (isEditing && robotCatalog) {
                return (
                    <Grid container spacing={0}>
                        <Grid item xs={12}>
                            <RCMonitorEditor
                                className={clsx('editing')}
                                isEditing={isEditing}
                                title={capitalizeAll(trans(selectedMonitor ? 'panel.edit_monitor.title' : 'panel.create_monitor.title'))}
                                icon="settings"
                                fields={fieldsSettings}
                                projectId={projectId}
                                robotCatalog={robotCatalog}
                                supportedTicketSystems={rcProjectSettings?.supportedTicketSystems}
                                slackChannels={rcProjectSettings?.slackSettings?.channels}
                                monitor={selectedMonitor}
                                preSelectedRobotClass={props.preSelectedRobotClass}
                                preSelectedRobotId={props.preSelectedRobotId}
                                preSelectedHomeId={props.preSelectedHomeId}
                                jumpToStep={editingStep}
                                actions={{
                                    startEdit: {
                                        onClick: () => {
                                            onChangeEditingMode(true);
                                        },
                                    },
                                    cancelEdit: {
                                        label  : capitalizeAll(trans('label.cancel')),
                                        onClick: onCancelEdit,
                                    },
                                    saveUpdate: {
                                        label  : capitalizeAll(trans('label.save')),
                                        onClick: onEditMonitorFormSubmit,
                                    },
                                }}
                            />
                        </Grid>
                    </Grid>
                );
            }
        }

        return (
            <CircularProgress />
        );
    }, [monitorActionsMap, monitorCreatedAgent, monitorRobot, monitorUpdatedAgent, editingStep, fieldsSettings, isEditing, onCancelEdit,
        onChangeEditingMode, onEditMonitorFormSubmit, projectId, props.monitorId, props.preSelectedHomeId, props.preSelectedRobotClass,
        props.preSelectedRobotId, rcProjectSettings, robotCatalog, selectedMonitor, trans]);


    return (
        <SlideOutComp
            className={classes.root}
            primaryActions={primaryActions}
            secondaryActions={secondaryActions}
            compContent={slideOutCompContent}
            closable={!isNewMonitor}
            contentContainerProps={{
                className: clsx('slide-out-comp-container', isEditing && 'is-editing'),
            }}
        />
    );
};
